import type { Socket } from 'socket.io-client';
import type { AppDispatch, RootState } from '../app/store';
import SOCKET from '../constants/socketTypes';
import { clearLoginAnnounced } from './announcedLogin';
import {
  selectJudge,
  updateJudgeLetter,
  updateJudgeName,
  updateJudgeUuid,
  updateLastCompletedTtUuid
} from '../redux/workSlice';

/**
 * Sign the current judge out of this phone: Home's Log out button, and the
 * scrutineer's remote log-out (Devices screen, "Log out all adjudicators").
 * Reads the store at call time, so it always logs out whoever is signed in now.
 */
export function logOutAdjudicator(
  socket: Socket | null,
  dispatch: AppDispatch,
  getState: () => RootState
): void {
  const state = getState();
  const judge = selectJudge(state);
  const { uuid: deviceUuid, deviceId } = state.settings;

  // Send the logout on the socket that actually owns the session, before
  // tearing it down. Emitting after disconnect()/connect() sent it from a new
  // socket carrying the old socketId, so the server's owner check failed and
  // the adjudicator was never properly logged out of its rooms.
  try {
    if (socket !== null && judge?.uuid) {
      socket.emit(SOCKET.ADJUDICATOR, {
        type: SOCKET.ADJUDICATOR_LOG_OUT,
        letter: judge.letter,
        name: judge.name,
        adjudicatorUuid: judge.uuid,
        socketId: socket.id,
        deviceUuid,
        deviceId
      });
      clearLoginAnnounced();
    }
  } catch (error) {
    console.error('Error in adjudicatorSocket:', error);
  }

  // Clear before reconnecting: SocketContext announces the stored judge on
  // 'connect', so a judge still in state here would be logged straight back
  // in by the reconnect below.
  dispatch(updateJudgeLetter(''));
  dispatch(updateJudgeName(''));
  dispatch(updateJudgeUuid(''));

  dispatch(updateLastCompletedTtUuid(null));

  try {
    if (socket !== null) {
      socket.disconnect();
      socket.connect();
    }
  } catch (error) {
    console.error('Error reconnecting socket after log out:', error);
  }
}
